import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEdit } from "react-icons/fa";
import { BsFiletypeCsv } from "react-icons/bs";
import { Plus, Trash2 } from "lucide-react";

import SeriesPopup from "./seriesPopup";
import Spinner from "../../components/spinner";
import AddedBy from "../../components/addedBy";
import FilterBar from "../../components/filterBar";
import SortBy from "../../components/sortBy";
import GenerateCSV from "../../components/popup/generateCSV";
import { useFilterChips } from "../../components/filterBar/useFilterChips";
import { convertToCloudFrontUrl, fetchAllGenresByLangOptions , fetchAllActorNames } from "../../helper";
import { formatMongoDBTime } from "../../utills/dateAndTime";
import { confirmCascadeDelete } from "../../helper/confirmCascadeDelete";
import { useLanguage } from "../../context/languageContext";
import { Can } from "../../context/permissionContext";
import { joinList } from "../../helper/listSeparator";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Series — ordered sets of books that belong together.
 *
 * These used to live at the foot of the Picks page, on the reasoning that an
 * admin arranging Picks should not have to go looking elsewhere. They are data
 * rather than an app screen, though, so they sit under Data with the other
 * catalogues now and Picks is only the screen it configures.
 */
const Series = () => {
  const {
    adminInfo,
    selectedLang,
    isShowCSVModalVisible,
    setIsShowCSVModalVisible,
    handleCloseCSV,
    handleGenerateCSV,
  } = useLanguage();

  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("latestCreated");

  const filters = useFilterChips({ supportsMatchAny: true, stickyField: "seriesName" });

  // Genre is a managed list, so the filter offers the stored names rather than
  // asking anyone to spell them.
  const [genreNames, setGenreNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllGenresByLangOptions("en", adminInfo.token).then((genres) => {
      if (!cancelled) setGenreNames(genres.map((g) => g.genreName));
    });
    return () => {
      cancelled = true;
    };
  }, [adminInfo?.token]);

  // Named as the table's own columns, so a filter says which column it
  // touches. Image, S.no and Action are not values to match on.

  // The names behind Created by / Edited by, so those filters are picked from
  // the team rather than spelled from memory.
  const [actorNames, setActorNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllActorNames(adminInfo.token).then((names) => {
      if (!cancelled) setActorNames(names);
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  const filterOptions = [
    { value: "seriesName", label: "Series Name" },
    { value: "bookName", label: "Books" },
    { value: "genreName", label: "Genre", suggestions: genreNames },
    { value: "createdAt", label: "Created on" },
    { value: "updatedAt", label: "Updated on" },
    { value: "createdBy", label: "Created by", suggestions: actorNames },
    { value: "updatedBy", label: "Edited by", suggestions: actorNames },
  ];
  // `editing`: false = closed, null = create, id = edit that one.
  const [editing, setEditing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // One object for the list AND the export. Built once so the CSV cannot
  // quietly describe a different query from the table it was generated from —
  // the two drifting apart is how an export starts handing back rows nobody
  // can see on screen.
  //
  // `signature` rather than the chips themselves, for the same reason the
  // fetch depended on it: it is debounced, so a chip being typed into does
  // not rebuild this — and therefore does not fire a request — per keystroke.
  const listFilters = useMemo(() => ({
    // One page of everything: there are a handful of series, and the table
    // is a list to arrange rather than a catalogue to page through.
    currentPage: 1,
    itemsPerPage: 200,
    langCode: selectedLang,
    sortBy,
    ...filters.chipFilters,
    matchMode: filters.matchMode,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [selectedLang, sortBy, filters.signature, filters.matchMode]);

  const fetchAllSeries = useCallback(async () => {
    try {
      const { data: response } = await axios.post(
        `${API}/api/admin/series/getall`,
        { filters: listFilters },
        { headers: { Authorization: `Bearer ${adminInfo?.token}` } }
      );

      if (response?.status) {
        setSeriesList(response?.data?.seriesDocs || []);
      } else {
        toast.error(response.message || "Could not load the series.");
      }
    } catch (error) {
      console.error("fetchAllSeries catch error::", error);
      toast.error("Could not load the series.");
    } finally {
      setLoading(false);
    }
  }, [adminInfo?.token, listFilters]);

  useEffect(() => {
    if (!adminInfo?.token) return;

    document.title = "Penspace - Series";
    // Language-scoped: the list request asks for one language's titles, so a
    // switch has to re-ask rather than relabel what is already on screen.
    fetchAllSeries();
  }, [adminInfo?.token, fetchAllSeries]);

  const handleDelete = async (row) => {
    const title = row?.translations?.title || "this series";

    const headers = { Authorization: `Bearer ${adminInfo?.token}` };
    // Recommenders list series beside books, so a delete takes it out of them —
    // said up front, like every other delete that cascades.
    if (!(await confirmCascadeDelete({
      source: "series",
      ids: [row._id],
      what: `"${title}"`,
      note: "The books in it are not affected.",
      headers,
    }))) {
      return;
    }

    setDeletingId(row._id);

    try {
      const { data: response } = await axios.put(
        `${API}/api/admin/series/delete`,
        { selectedSeriesIds: [row._id] },
        { headers }
      );

      if (response?.status) {
        toast.success(response.message || "Series moved to the trash.");
        fetchAllSeries();
      } else {
        toast.error(response.message || "Could not delete the series.");
      }
    } catch (error) {
      console.error("handleDelete catch error::", error);
      toast.error("Could not delete the series.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <Spinner />;

  return (
    <React.Fragment>
      <div className="heading">
        <div>
          <h4 className="mb-0">
            Series{seriesList.length > 0 ? ` (${seriesList.length})` : ""}
          </h4>
          <small className="text-muted">
            Ordered sets of books that belong together. The order you arrange the books in is
            the order the app reads them in.
          </small>
        </div>
        {/* CSV beside Add, as on Books: the row below is the sort control on
            the left and the pager on the right. */}
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={() => setIsShowCSVModalVisible(true)}
          >
            Generate CSV <BsFiletypeCsv />
          </button>
          <Can feature="series" action="create">
            <button type="button" className="btn btn-dark" onClick={() => setEditing(null)}>
              <Plus size={18} /> Add Series
            </button>
          </Can>
        </div>
      </div>

      {/* The same toolbar row every other list uses (Books, Summaries,
          Collections, Authors, Recommends): the ordering control on the left,
          the pager on the right. The right half is kept free for the pager.

          Above the filter bar, as on those same pages — Series was the one
          list that had the two the other way round. */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center my-1">
        <SortBy value={sortBy} onChange={setSortBy} />
      </div>

      <FilterBar
        options={filterOptions}
        supportsMatchAny
        chips={filters.chips}
        matchMode={filters.matchMode}
        onMatchModeChange={filters.setMatchMode}
        onAdd={filters.addChip}
        onUpdate={filters.updateChip}
        onRemove={filters.removeChip}
        onClear={filters.clearChips}
      />

      {/* The shared table chrome every other list uses — `table_scrollbar`
          and `table` are global classes in App.css, which is where the
          padding, sticky head and row rules live. A module class of the same
          name gets none of them, which is why this table arrived with its
          headings run together and no padding at all. */}
      <div className="table_scrollbar table-responsive mt-1">
        <table className="table">
          <thead>
            <tr>
              <th className="index">S.no</th>
              <th className="image">Image</th>
              <th className="max_width">Series Name</th>
              <th className="max_width">Description</th>
              <th className="max_width">Books</th>
              <th className="max_width">Authors</th>
              <th className="mid_width">Genre</th>
              <th className="min_width">Created on</th>
              <th className="min_width">Updated on</th>
              <th className="min_width">Added by</th>
              <th className="action">Action</th>
            </tr>
          </thead>
          <tbody>
            {seriesList.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center text-muted py-4">
                  No series yet. Add one with the button above.
                </td>
              </tr>
            ) : (
              seriesList.map((row, index) => (
                <tr key={row._id}>
                  <td className="index">{index + 1}</td>
                  {/* A series has no cover of its own, so this is its FIRST
                      book's — served as `firstBookCover` by the list. The
                      series' own coverImage is still preferred where one has
                      been set. */}
                  <td className="image">
                    {(() => {
                      const cover =
                        row.coverImage?.uploadedImageUrl ||
                        row.firstBookCover?.uploadedImageUrl;

                      return cover ? (
                        <img src={convertToCloudFrontUrl(cover)} alt="" />
                      ) : (
                        <p className="image-skeleton" aria-hidden="true">
                          <span>NO COVER</span>
                        </p>
                      );
                    })()}
                  </td>
                  <td className="max_width">{row?.translations?.title || "(untitled)"}</td>
                  <td className="tooltip_content">
                    <span>{row?.translations?.description || "—"}</span>
                  </td>
                  {/* The book NAMES, not a count: which books are in a series is
                      the thing worth seeing at a glance. */}
                  <td className="max_width">
                    {(row?.translations?.books || []).length > 0
                      ? (row.translations.books || []).map((book) => (
                          <li className="list_styled" key={book.bookId}>{book.bookName}</li>
                        ))
                      : "—"}
                  </td>
                  {/* A book is never shown without its author: one line per book,
                      level with the Books column beside it. */}
                  <td className="max_width">
                    {(row?.translations?.books || []).length > 0
                      ? (row.translations.books || []).map((book) => (
                          <li className="list_styled" key={book.bookId}>
                            {book.authorNames?.length ? joinList(book.authorNames) : "—"}
                          </li>
                        ))
                      : "—"}
                  </td>
                  <td className="mid_width">
                    {(row?.translations?.genres || []).length > 0
                      ? joinList((row.translations.genres || []).map((genre) => genre.genreName))
                      : "—"}
                  </td>
                  <td className="min_width">
                    {row.createdAt ? formatMongoDBTime(row.createdAt) : "—"}
                  </td>
                  <td className="min_width">
                    {row.updatedAt ? formatMongoDBTime(row.updatedAt) : "—"}
                  </td>
                  <td className="min_width">
                    <AddedBy record={row} />
                  </td>
                  <td className="action">
                    <div className="d-inline-flex align-items-center gap-1">
                      <Can feature="series" action="edit">
                        <div title="Edit" onClick={() => setEditing(row._id)}>
                          <FaEdit size={20} />
                        </div>
                      </Can>
                      <Can feature="series" action="delete">
                        <div
                          title="Delete series"
                          onClick={() => (deletingId ? null : handleDelete(row))}
                        >
                          <Trash2 size={20} />
                        </div>
                      </Can>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Keyed so the form is a fresh one per series — otherwise the last one
          edited leaks its books into the next. */}
      {editing !== false && (
        <SeriesPopup
          key={editing ?? "new"}
          isVisible
          seriesId={editing}
          handleCloseModal={() => setEditing(false)}
          onSuccess={fetchAllSeries}
        />
      )}

      {/* The export carries the list's own filters, so the file is the rows
          on screen rather than the whole collection. */}
      <GenerateCSV
        title="series"
        isShowCSVModalVisible={isShowCSVModalVisible}
        handleCloseCSV={handleCloseCSV}
        handleGenerateCSV={() => handleGenerateCSV("series", { filters: listFilters })}
      />
    </React.Fragment>
  );
};

export default Series;
